import React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Calendar,
  Clock,
  User2,
  Loader2,
} from "lucide-react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/cmsConstants";
import { RichView } from "@/components/cms/PublicContentCard";

/*
 * Public blog article page at `/blog/:slug`.
 * Renders the full sanitized HTML body via `RichView`.
 */
export default function BlogDetailPage() {
  const { slug } = useParams();
  const q = useQuery({
    queryKey: ["public-cms", "blog", slug],
    queryFn: () => api.publicBlogDetail(slug),
    retry: 0,
  });
  const blog = q.data;
  const f = blog?.fields || {};

  return (
    <div className="min-h-screen bg-white text-[#2c2c3e]">
      <header className="border-b border-[#e0e0e8] bg-white sticky top-0 z-10">
        <div className="mx-auto max-w-[820px] px-4 md:px-6 h-[64px] flex items-center gap-3">
          <Link
            to="/blog"
            data-testid="blog-back-list"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#6b6bb5] hover:underline"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Blog
          </Link>
          <div className="ml-auto">
            <Link
              to="/login"
              className="h-9 px-4 rounded-md text-sm font-medium inline-flex items-center hover:opacity-90"
              style={{ background: "#1a1a2e", color: "#fff" }}
            >
              Login
            </Link>
          </div>
        </div>
      </header>

      <main
        className="mx-auto max-w-[820px] px-4 md:px-6 py-8 md:py-12"
        data-testid="blog-detail-page"
      >
        {q.isLoading ? (
          <div className="py-20 text-center text-[#6b6bb5]">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
            Loading article…
          </div>
        ) : q.isError || !blog ? (
          <div className="py-20 text-center">
            <div className="text-[15px] font-semibold text-[#1a1a2e]">
              Article not found
            </div>
            <div className="text-[13px] text-[#6b6bb5] mt-1">
              The blog post may have been unpublished.
            </div>
            <Link
              to="/blog"
              className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#6b6bb5] hover:underline"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Return to blog list
            </Link>
          </div>
        ) : (
          <article>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {(blog.category || []).map((c) => (
                <span
                  key={c}
                  className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded"
                  style={{ background: "#2d2d7a", color: "#fff" }}
                >
                  {c}
                </span>
              ))}
            </div>

            <h1 className="text-3xl md:text-[38px] font-bold text-[#1a1a2e] leading-tight tracking-tight">
              {blog.title}
            </h1>

            {f.metaDescription && (
              <p className="mt-3 text-[15px] text-[#6b6bb5] leading-snug">
                {f.metaDescription}
              </p>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-4 text-[12px] text-[#888] pb-6 mb-6 border-b border-[#e0e0e8]">
              {f.author && (
                <span className="inline-flex items-center gap-1.5">
                  <User2 className="h-3.5 w-3.5 text-[#6b6bb5]" />
                  <span className="text-[#1a1a2e] font-medium">
                    {f.author}
                  </span>
                  {f.designation && (
                    <span className="text-[#888]">· {f.designation}</span>
                  )}
                </span>
              )}
              {f.publishDate && (
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-[#6b6bb5]" />
                  {formatDate(f.publishDate)}
                </span>
              )}
              {f.readingTime && (
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-[#6b6bb5]" />
                  {f.readingTime} min read
                </span>
              )}
            </div>

            {f.coverImage && (
              <img
                src={f.coverImage}
                alt=""
                className="w-full max-h-[420px] object-cover rounded-lg mb-8"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            )}

            <RichView html={f.body || ""} className="text-[15px] cms-article" />

            {f.tags && (
              <div className="mt-10 pt-6 border-t border-[#e0e0e8] flex flex-wrap gap-1.5">
                {f.tags
                  .split(",")
                  .map((t) => t.trim())
                  .filter(Boolean)
                  .map((t) => (
                    <span
                      key={t}
                      className="text-[11px] px-2.5 py-1 rounded-md bg-[#f5f5f8] text-[#2d2d7a]"
                    >
                      #{t}
                    </span>
                  ))}
              </div>
            )}
          </article>
        )}
      </main>
    </div>
  );
}
